import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Video, Music, BookOpen, FileText, Play, FileDown, Upload } from 'lucide-react';
import { HERO_IMAGES, TEACHINGS, DAILY_DOSAGES } from '../mock';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import VideoModal from '../components/VideoModal';
import MaterialUploadDialog from '../components/MaterialUploadDialog';
import api, { fileUrl } from '../lib/api';

const KINDS = [
  { key: 'all', label: 'All' },
  { key: 'video', label: 'Video', Icon: Video },
  { key: 'audio', label: 'Audio', Icon: Music },
  { key: 'dosage', label: 'Dosages', Icon: BookOpen },
  { key: 'material', label: 'Materials', Icon: FileText },
];

export default function Archive() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const [videoOpen, setVideoOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.listTeachings(), api.listDosages(), api.listMaterials()]).then(([t, d, m]) => {
      const teachVideos = t.filter(x => x.video_url).map(x => ({
        id: `v-${x.id}`, kind: 'video', title: x.title, subtitle: x.speaker, series: x.series,
        image: x.thumbnail_url ? fileUrl(x.thumbnail_url) : HERO_IMAGES.crossSunset,
        videoUrl: fileUrl(x.video_url), audioUrl: x.audio_url ? fileUrl(x.audio_url) : null,
        pdfUrl: x.pdf_url ? fileUrl(x.pdf_url) : null, createdAt: x.created_at,
      }));
      const teachAudios = t.filter(x => x.audio_url).map(x => ({
        id: `a-${x.id}`, kind: 'audio', title: x.title, subtitle: x.speaker, series: x.series,
        image: x.thumbnail_url ? fileUrl(x.thumbnail_url) : HERO_IMAGES.bibleSunrise,
        audioUrl: fileUrl(x.audio_url), createdAt: x.created_at,
      }));
      const mockVideos = TEACHINGS.filter(t => t.isLocal).map(t => ({
        id: `v-mock-${t.id}`, kind: 'video', title: t.title, subtitle: t.speaker, series: t.series,
        image: t.thumbnail, videoUrl: t.videoUrl, mock: true,
      }));
      const dItems = d.map(x => ({
        id: `d-${x.id}`, kind: 'dosage', title: x.title, subtitle: x.verse_ref, series: 'Dosage',
        image: x.image_url ? fileUrl(x.image_url) : HERO_IMAGES.bibleSunrise, createdAt: x.created_at,
      }));
      const mockDosages = DAILY_DOSAGES.map(x => ({
        id: `d-mock-${x.id}`, kind: 'dosage', title: x.title, subtitle: x.verseRef, series: 'Dosage',
        image: x.image, mock: true,
      }));
      const matItems = m.map(x => ({
        id: `m-${x.id}`, kind: 'material', title: x.title, subtitle: x.file_name, series: 'Material',
        image: HERO_IMAGES.bibleStudy, fileUrl: fileUrl(x.file_url), fileType: x.file_type,
        description: x.description, createdAt: x.created_at,
      }));
      setItems([...teachVideos, ...teachAudios, ...dItems, ...matItems, ...mockVideos, ...mockDosages]);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(i => (kind === 'all' || i.kind === kind) && (!q || i.title.toLowerCase().includes(q) || (i.subtitle || '').toLowerCase().includes(q)));
  }, [items, kind, query]);

  const onClick = (i) => {
    if (i.kind === 'video') { setActive({ videoUrl: i.videoUrl, title: i.title, isLocal: true }); setVideoOpen(true); }
    if (i.kind === 'material' && i.fileUrl) window.open(i.fileUrl, '_blank');
    if (i.kind === 'audio' && i.audioUrl) window.open(i.audioUrl, '_blank');
  };

  return (
    <div>
      <section className="relative pt-32 pb-14 md:pt-40 md:pb-20">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.bibleStudy} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/45" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Everything, In One Place</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">Ministry Archive</h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl">Every teaching, every dosage, every material — searchable in one library.</p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-6">
            <div className="flex flex-wrap gap-2">
              {KINDS.map(k => {
                const Icon = k.Icon;
                return (
                  <button key={k.key} onClick={() => setKind(k.key)} className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${kind === k.key ? 'bg-teal-deep text-white' : 'bg-white text-ink/70 border border-ink/10 hover:border-teal-brand hover:text-teal-deep'}`}>
                    {Icon && <Icon className="h-3.5 w-3.5" />} {k.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
                <Input placeholder="Search archive…" value={query} onChange={e => setQuery(e.target.value)} className="pl-9 bg-white" />
              </div>
              <Button onClick={() => setUploadOpen(true)} className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold whitespace-nowrap h-10">
                <Upload className="h-4 w-4 mr-1.5" /> Upload Material
              </Button>
            </div>
          </div>
          <p className="text-xs text-ink/50 mb-8">Have a study guide, sermon notes, or article to share? Upload it to the archive — passcode required.</p>

          {filtered.length === 0 ? (
            <p className="text-center text-ink/50 py-16">Nothing matches your search yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(i => {
                const Icon = i.kind === 'video' ? Play : i.kind === 'audio' ? Music : i.kind === 'dosage' ? BookOpen : FileText;
                const isClickable = i.kind === 'video' || i.kind === 'material' || i.kind === 'audio';
                const CardTag = i.kind === 'dosage' ? Link : 'div';
                const cardProps = i.kind === 'dosage' ? { to: '/dosage' } : {};
                return (
                  <CardTag key={i.id} {...cardProps}>
                    <Card className={`hover-lift group overflow-hidden bg-white rounded-2xl border border-ink/5 ${isClickable ? 'cursor-pointer' : ''}`} onClick={() => isClickable && onClick(i)}>
                      <div className="relative aspect-video overflow-hidden bg-black">
                        <img src={i.image} alt={i.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <Icon className="h-6 w-6 text-teal-deep" />
                          </div>
                        </div>
                        <Badge className="absolute top-3 left-3 bg-gold-brand text-ink font-medium capitalize">{i.series || i.kind}</Badge>
                      </div>
                      <div className="p-5">
                        <h3 className="font-serif-display text-xl leading-snug text-ink group-hover:text-teal-deep transition-colors">{i.title}</h3>
                        {i.subtitle && <p className="text-xs tracking-[0.15em] uppercase text-ink/50 mt-2">{i.subtitle}</p>}
                      </div>
                    </Card>
                  </CardTag>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoUrl={active?.videoUrl} title={active?.title} isLocal={active?.isLocal} />
      <MaterialUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
    </div>
  );
}
